import { getInternationalWeekDateTz } from '@utils/native-dates';
import { v2Links } from '@utils/navigation/links';
import {
  TeamCalendarGame,
  TeamCalendarResponse,
  TeamGame,
} from '@v2/services/team-schedule';
import {
  addMonths,
  differenceInCalendarMonths,
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfMonth,
  format,
  getDay,
  parseISO,
  startOfMonth,
} from 'date-fns';

export interface TeamCalendarRange {
  startDate: string;
  endDate: string;
}

export const getDefaultTeamCalendarRange = (startDate: string) => ({
  startDate,
  endDate: format(endOfMonth(addMonths(parseISO(startDate), 2)), 'yyyy-MM-dd'),
});

export const getTeamCalendarPageRange = (
  range: TeamCalendarRange,
  pageIndex: number,
  allowMore: boolean
): TeamCalendarRange | null => {
  if (
    range.endDate < range.startDate ||
    differenceInCalendarMonths(
      parseISO(range.endDate),
      parseISO(range.startDate)
    ) >= 12
  ) {
    return null;
  }
  if (pageIndex === 0) return range;
  if (!allowMore) return null;

  const nextMonth = addMonths(startOfMonth(parseISO(range.endDate)), pageIndex);
  if (differenceInCalendarMonths(nextMonth, parseISO(range.startDate)) >= 12)
    return null;

  return {
    startDate: format(nextMonth, 'yyyy-MM-dd'),
    endDate: format(endOfMonth(nextMonth), 'yyyy-MM-dd'),
  };
};

export const mergeTeamCalendarPages = (pages: TeamCalendarResponse[]) => ({
  games: Array.from(
    new Map(
      pages.flatMap((page) => page.games).map((game) => [game.eventId, game])
    ).values()
  ).sort(
    (first, second) =>
      first.date.localeCompare(second.date) ||
      first.occursAtLocal.localeCompare(second.occursAtLocal) ||
      first.eventId - second.eventId
  ),
  opponents: Array.from(
    new Map(
      pages
        .flatMap((page) => page.opponents)
        .map((opponent) => [opponent.id, opponent])
    ).values()
  ).sort((first, second) => first.name.localeCompare(second.name)),
});

export const buildTeamCalendarMonths = (
  range: TeamCalendarRange,
  games: TeamCalendarGame[]
) => {
  const gamesByDate = new Map<string, TeamCalendarGame[]>();
  for (const game of games) {
    gamesByDate.set(game.date, [...(gamesByDate.get(game.date) ?? []), game]);
  }

  return eachMonthOfInterval({
    start: parseISO(range.startDate),
    end: parseISO(range.endDate),
  }).map((month) => {
    const days = eachDayOfInterval({
      start: month,
      end: endOfMonth(month),
    }).map((day) => {
      const date = format(day, 'yyyy-MM-dd');
      return { date, day: day.getDate(), games: gamesByDate.get(date) ?? [] };
    });
    const cells = [...Array<null>(getDay(month)).fill(null), ...days];
    while (cells.length % 7) cells.push(null);

    return {
      month: format(month, 'yyyy-MM'),
      label: format(month, 'MMMM yyyy'),
      weeks: Array.from({ length: cells.length / 7 }, (_, index) =>
        cells.slice(index * 7, (index + 1) * 7)
      ),
    };
  });
};

export const formatTeamPrice = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);

export const getTeamGameEventLink = ({
  name,
  occursAtLocal,
}: Pick<TeamGame, 'name' | 'occursAtLocal'>) => {
  const eventDate = getInternationalWeekDateTz(occursAtLocal, true).replaceAll(
    ',',
    ''
  );
  return `${name}-${eventDate}`.replaceAll(' ', '-').replaceAll('.', '');
};

export const getTeamGameHref = (game: TeamGame) =>
  `${v2Links.buy}?${new URLSearchParams({ eventId: String(game.eventId), eventLink: getTeamGameEventLink(game) })}`;
